<template>
  <div
    class="insights-unusual-activity-widget insights-widget"
    :class="expandedClass"
  >
    <InsightsUnusualActivityMemberDialog
      v-if="showDialog"
      show
      :time-zone="timeZone"
      :dialog-date="dialogDate"
      @close="closeDialog"
      @select="onActivitySelected"
    />

    <div class="d-flex justify-content-between unusual-activity-header mb-20 mt-40">
      <div class="text-18 z-text-gray-900">
        <div v-if="isUserDashboard && currentDashboardUser" class="title strong">
          {{ currentDashboardUser.name }}'s unusual activity
        </div>
        <div v-else class="title strong">Members with unusual activity</div>
        <div class="text-13 z-text-gray-600">
          {{ tooltipContent }}
        </div>
      </div>
      <div
        v-if="insightsAvailable"
        class="d-flex align-items-center unusual-activity-actions -mr-20"
      >
        <AppButton
          v-if="!isUserDashboard && !teamId"
          v-tooltip="markAllButtonTooltip"
          :loading="isMarkingAllReviewed"
          :disabled="isMarkingAllReviewed || !hasUnseenHighConfidenceActivities"
          :class="{ 'opacity-50': !hasUnseenHighConfidenceActivities }"
          class="unusual-activity-mark-all-button"
          type="secondary"
          modifiers="d-flex align-items-center"
          @click="markAllAsReviewed"
        >
          <span class="material-symbols-rounded bold hi-16 mr-8">check_circle</span>
          Mark all as reviewed
        </AppButton>

        <div class="ml-12">
          <AppExportMenu
            text="Export report"
            hide-caret
            type-classes="btn btn-naked d-flex align-items-center"
            icon-classes="hi hi-export hi-16"
          >
            <AppExportMenuItem>
              <a
                v-track:click="{ label: 'Unusual export', format: 'csv' }"
                :href="exportHref('csv')"
                data-qa="unusual-activity-export-csv"
                @click.prevent="downloadExport('csv')"
              >
                To CSV
              </a>
            </AppExportMenuItem>
            <AppExportMenuItem v-if="isUserDashboard">
              <a
                v-track:click="{ label: 'Unusual export', format: 'pdf' }"
                :href="exportHref('pdf')"
                data-qa="unusual-activity-export-pdf"
                @click.prevent="downloadExport('pdf')"
              >
                To PDF
              </a>
            </AppExportMenuItem>
          </AppExportMenu>
        </div>
      </div>
    </div>

    <AppExporterWebsockets ref="exporterWs" />

    <!-- loading -->
    <div
      v-if="firstLoad && isLoading"
      class="widget-wrapper"
    >
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-body widget-loading" />
      </div>
    </div>

    <template v-if="!firstLoad">
      <InsightsUnusualActivityWidgetTableWrapper
        :data="data"
        :empty-reason="emptyReason"
        :has-table-data="!!hasTableData"
        :is-loading="isLoading"
        :pagination="pagination"
        :set-page="setPage"
        :sort-options="sortOptions"
        :sort="sort"
        :switch-member="switchMember"
        :time-zone="timeZone"
        :open-dialog="openDialog"
      />
    </template>
  </div>
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { Insights, InsightsFilters } from '~/mixins';
import { UnusualActivitiesResource } from '~/resources';
import { mapGetters, mapState } from 'vuex';

import InsightsUnusualActivityWidgetTableWrapper from '~/components/insights/InsightsUnusualActivityWidgetTableWrapper.vue';
import InsightsUnusualActivityMemberDialog from '~/components/insights/InsightsUnusualActivityMemberDialog.vue';
import { pickBy } from 'lodash-es';
import { coverQueryString } from '~/utils/querystring_utils';
import AppButton from '~/components/AppButton.vue';
import AppExporterWebsockets from '~/components/AppExporterWebsockets.vue';
import AppExportMenu from '~/components/AppExportMenu.vue';
import AppExportMenuItem from '~/components/AppExportMenuItem.vue';

const WIDGET_KEY = INSIGHTS.api.unusualActivity.team;

export default {
  name: 'InsightsUnusualActivityWidget',
  components: {
    AppButton,
    AppExporterWebsockets,
    AppExportMenu,
    AppExportMenuItem,
    InsightsUnusualActivityMemberDialog,
    InsightsUnusualActivityWidgetTableWrapper,
  },
  mixins: [Insights, InsightsFilters],
  props: {
    timeZone: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    firstLoad: {
      type: Boolean,
      required: true,
    },
    period: {
      type: String,
      default: null,
    },
    pagination: {
      type: Object,
      required: true,
    },
    setPage: {
      type: Function,
      required: true,
    },
    sort: {
      type: Function,
      required: true,
    },
    sortOptions: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    dialogDate: null, // to open dialog on a specific date
    investigateClicked: Object.freeze({ label: 'Unusual Investigate now clicked' }),
    isMarkingAllReviewed: false,
    monthlyTotal: 0,
    showDialog: false,
    tooltipOptions: Object.freeze({ html: true, sanitize: false }),
    tooltipContent: INSIGHTS.tooltips.unusualActivity.content,
    widgetKey: WIDGET_KEY,
  }),
  computed: {
    ...mapState({
      organizationInsightsUrl: 'organization_insights_url',
    }),
    ...mapGetters([
      'currentDashboardId',
      'currentDashboardUser',
      'dashboardType',
      'filters',
      'insightsAvailable',
      'isIndividualTeamDashboard',
      'isUserDashboard',
      'organizationId',
      'queryDate',
      'startWeekOn',
    ]),
    noDataEmptyReason() {
      return this.emptyReason === 'no_data';
    },
    hasTableData() {
      const key = this.isUserDashboard ? 'unusual_activities' : 'users';

      return this.data?.[key] && Object.keys(this.data[key]).length;
    },
    userId() {
      return this.currentDashboardUser?.id;
    },
    teamId() {
      return this.filters?.team_id;
    },
    totalIncidents() {
      return this.chart ? this.chart.instances : 0;
    },
    chart() {
      return this.isRequestedDataPresent && this.data ? this.data.stats : { x: 1 };
    },
    users() {
      return this.data?.users || [];
    },
    memberDialogTitle() {
      return this.currentDashboardUser ? `${this.currentDashboardUser.name}'s unusual activity` : `Unusual activity`;
    },
    expandedClass() {
      return this.expanded ? 'expanded' : null;
    },
    isRequestedDataPresent() {
      return this.period === 'requested' || !this.period;
    },
    exportURL() {
      return `${document.location.origin}/organizations/${this.organizationId}/unusual_activities/export`;
    },
    exportURLParams() {
      return {
        from: this.filters.date.from,
        to: this.filters.date.to,
        team_id: this.filters.team_id,
        user_id: this.filters.user_id,
        time_zone: this.filters.time_zone,
        confidence_levels: this.filters.confidence_levels,
        dashboard_type: this.dashboardType,
      };
    },
    hasUnseenHighConfidenceActivities() {
      if (this.isUserDashboard) {
        return false;
      }

      const users = this.data?.users || [];

      return users.some((user) => user.pending_notification === true);
    },
    markAllButtonTooltip() {
      if (this.isUserDashboard) {
        return '';
      }

      if (!this.hasUnseenHighConfidenceActivities) {
        return 'All high-confidence unusual activities have been reviewed';
      }

      return 'Mark all users with high-confidence unusual activities as reviewed';
    },
  },
  mounted() {
    if (this.filters.starts_at) {
      this.dialogDate = this.filters.starts_at;
      this.showDialog = true;
    }
  },
  methods: {
    exportHref(format) {
      const params = pickBy({ ...this.exportURLParams, format }, (v) => v != null && v !== '');
      const query = coverQueryString(params);

      return query ? `${this.exportURL}?${query}` : this.exportURL;
    },
    downloadExport(format) {
      if (this.$refs.exporterWs?.isQueued) {
        return;
      }

      const url = this.exportHref(format);

      this.$refs.exporterWs.download(format, { url, background: true });
    },
    syncFiltersToUrl(changes) {
      // These are UI-only scope keys (dialog date, screenshot index, activity type) that the widget
      // container excludes from its :key, so they must not trigger a widget refetch - skipWidgets
      // keeps the old raw-commit behavior while still routing through the single setFilter action.
      this.setFilter({ changes, skipWidgets: true });
    },
    openDialog(date) {
      this.dialogDate = date;
      this.showDialog = true;
      this.syncFiltersToUrl({ starts_at: date });
    },
    closeDialog() {
      this.showDialog = false;
      // activity_type: cleared for legacy bookmarked URLs that include it
      this.syncFiltersToUrl({ starts_at: null, activity_type: null, index: null });
    },
    onActivitySelected(index) {
      this.syncFiltersToUrl({ index });
    },
    switchMember(id) {
      const string = `${id}_user_id`;

      this.setPreviousView({ previousView: this.selectedFilter, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
    async markAllAsReviewed() {
      if (!this.hasUnseenHighConfidenceActivities || this.isMarkingAllReviewed) {
        return;
      }

      this.isMarkingAllReviewed = true;

      try {
        const response = await UnusualActivitiesResource.markAllReviewed({ organizationId: this.organizationId });

        if (response.success) {
          window.location.reload();
        } else {
          console.warn('Failed to mark all unusual activities as reviewed:', response.message);
        }
      } catch (error) {
        console.warn('Error marking all unusual activities as reviewed:', error);
      } finally {
        this.isMarkingAllReviewed = false;
      }
    },
  },
};
</script>
